// Workflows: saved DAG pipelines of subagent stages that lattice itself
// runs (docs/workflows-plan.md). A workflow is a spec (stages, parameters,
// data sources, settings) kept by routes/workflows.py; a run of it is
// read from routes/workflow_runs.py. Every refusal comes back as
// `{detail: {code, message}}`, which `workflowError` shows and
// `workflowErrorCode` reads. Every query sits under ["workflows", c].

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { forgetFirstWorkflowWords } from "@/features/workflows/list/firstWorkflowWords";
import { ApiError, apiFetch, rawFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

import { blueprintKeys } from "./subagentBlueprints";

export type WorkflowSpec = components["schemas"]["WorkflowSpec"];
export type StageSpec = components["schemas"]["StageSpec"];
export type StageInputs = components["schemas"]["StageInputs"];
export type ParameterSpec = components["schemas"]["ParameterSpec"];
export type ParameterType = ParameterSpec["type"];
export type FanOutSpec = components["schemas"]["FanOutSpec"];
export type SourcesSpec = components["schemas"]["SourcesSpec"];
export type WorkflowSettings = components["schemas"]["WorkflowSettings"];
/** A timetable the engine starts runs on, the email it sends when a run ends, and where a schedule stands. */
export type ScheduleSpec = components["schemas"]["ScheduleSpec"];
export type FinishEmail = components["schemas"]["FinishEmail"];
export type ScheduleState = components["schemas"]["ScheduleState"];
export type SaveWorkflowBody = components["schemas"]["SaveWorkflowBody"];
export type StartRunBody = components["schemas"]["StartRunBody"];
export type RunSummary = components["schemas"]["RunSummary"];
/** A started run; `task_id` (= `run_id`) when it was started as a task. */
export type StartRunResponse = components["schemas"]["StartRunResponse"];
export type RunCounters = components["schemas"]["RunCounters"];
export type RunListResponse = components["schemas"]["RunListResponse"];
export type TemplateSummary = components["schemas"]["TemplateSummary"];
export type TemplateSubagent = components["schemas"]["TemplateSubagent"];
export type TemplateListResponse = components["schemas"]["TemplateListResponse"];
export type InstallTemplateBody = components["schemas"]["InstallTemplateBody"];
export type InstallTemplateResponse = components["schemas"]["InstallTemplateResponse"];
/** A workflow as its card draws it (views.schematic): the stages, what feeds them, the latest runs' outcomes. */
export type WorkflowSchematic = components["schemas"]["WorkflowSchematic"];
export type SchematicStage = components["schemas"]["SchematicStage"];
export type SchematicRun = components["schemas"]["SchematicRun"];
// Round 2 (docs/workflows-round2-plan.md §3-§5): what a stage is allocated,
// a subagent defined inside the workflow, and the run's handover pack.
export type StageResources = components["schemas"]["StageResources"];
export type StageAgent = components["schemas"]["StageAgent"];
export type HandoverSpec = components["schemas"]["HandoverSpec"];
export type ThinkingLevel = NonNullable<StageResources["thinking"]>;
export type ModelOption = components["schemas"]["ModelOption"];
export type ModelsResponse = components["schemas"]["ModelsResponse"];
/** What a save could not do, or a Check found missing (WorkflowSummary.notes): grouped by kind, on its stage. */
export type SaveNote = components["schemas"]["SaveNote"];

/** A data-source file lattice holds for one workflow (…/workflows/{slug}/files). */
export interface WorkflowFile {
  name: string;
  size: number;
  path: string;
  modified: string;
}

// `warnings` (data sources a save could not scope to the stages' subagents)
// and `files` are on the backend branch and not in api.gen.ts yet: typed
// here until the next codegen, optional so an older api still reads.
export type WorkflowSummary = components["schemas"]["WorkflowSummary"] & { warnings?: string[] };
export type WorkflowListResponse = Omit<components["schemas"]["WorkflowListResponse"], "workflows"> & {
  workflows: WorkflowSummary[];
};
export type WorkflowDetail = components["schemas"]["WorkflowDetail"] & { files?: WorkflowFile[] };

/** A run that is not over yet (schema.LIVE_RUN). */
export function isLiveRunStatus(status: string): boolean {
  return status === "queued" || status === "running" || status === "paused";
}

const base = (c: string) => `/api/customers/${encodeURIComponent(c)}/workflows`;
const one = (c: string, slug: string) => `${base(c)}/${encodeURIComponent(slug)}`;

export const workflowKeys = {
  all: (c: string | null) => ["workflows", c] as const,
  list: (c: string | null) => ["workflows", c, "list"] as const,
  one: (c: string | null, slug: string | null) => ["workflows", c, "one", slug] as const,
  files: (c: string | null, slug: string | null) => ["workflows", c, "files", slug] as const,
  /** `limit`: how many it asked for (0 = the server's default, 100); a reader asking for more has its own entry. */
  runs: (c: string | null, workflow: string | null, live: boolean, limit = 0) => ["workflows", c, "runs", workflow ?? "", live ? "live" : "all", limit] as const,
  templates: (c: string | null) => ["workflows", c, "templates"] as const,
  models: (c: string | null) => ["workflows", c, "models"] as const,
  storage: (c: string | null) => ["workflows", c, "storage"] as const,
  /** Its steps' examples of good work (api/examples.ts), kept beside the saved workflow. */
  examples: (c: string | null, slug: string | null) => ["workflows", c, "examples", "workflow", slug] as const,
};

const notFound = (error: unknown) => error instanceof ApiError && error.status === 404;

/** Every workflow of the workspace with its last run, and whether making one is offered. */
export function useWorkflows(customerId: string | null) {
  return useQuery({
    queryKey: workflowKeys.list(customerId),
    enabled: !!customerId,
    queryFn: async ({ signal }) => {
      const list = await apiFetch<WorkflowListResponse>(base(customerId!), { signal });
      if (list.workflows.length > 0) forgetFirstWorkflowWords(customerId!);
      return list;
    },
    staleTime: 5_000,
    // A run ends on its own: while one is live the cards' last run follows it;
    // with nothing running there is nothing to follow (starting a run re-reads).
    refetchInterval: (query) => ((query.state.data?.workflows ?? []).some((w) => w.last_run && isLiveRunStatus(w.last_run.status)) ? 30_000 : false),
    refetchOnWindowFocus: true,
  });
}

/** One workflow's detail (its spec, missing subagents, files, runs); the queryFn of useWorkflow and of lists that need specs. */
export function fetchWorkflow(customerId: string, slug: string, signal?: AbortSignal): Promise<WorkflowDetail> {
  return apiFetch<WorkflowDetail>(one(customerId, slug), { signal });
}

export function useWorkflow(customerId: string | null, slug: string | null) {
  return useQuery({
    queryKey: workflowKeys.one(customerId, slug),
    enabled: !!customerId && !!slug,
    queryFn: ({ signal }) => fetchWorkflow(customerId!, slug!, signal),
    staleTime: 5_000,
    // A missing workflow is an answer, not a blip to retry.
    retry: (count, error) => !notFound(error) && count < 1,
  });
}

function invalidateWorkflows(qc: QueryClient, customerId: string, slug?: string) {
  void qc.invalidateQueries({ queryKey: workflowKeys.list(customerId) });
  void qc.invalidateQueries({ queryKey: workflowKeys.templates(customerId) });
  if (slug) void qc.invalidateQueries({ queryKey: workflowKeys.one(customerId, slug) });
  // a step saved for the first time gets its examples, a removed one loses them
  if (slug) void qc.invalidateQueries({ queryKey: workflowKeys.examples(customerId, slug) });
}

/**
 * A save can make subagents (a step's own, the report's writer) and the editor
 * says from this list what is set up: the save is not over until the list is
 * read again, or the page calls what it just made missing. Never held for long.
 */
const LIST_WAIT_MS = 5000;
function afterSave(qc: QueryClient, customerId: string, slug: string): Promise<unknown> {
  invalidateWorkflows(qc, customerId, slug);
  return Promise.race([qc.invalidateQueries({ queryKey: blueprintKeys.list(customerId) }), new Promise((done) => window.setTimeout(done, LIST_WAIT_MS))]);
}

export function useCreateWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, spec }: { customerId: string; spec: WorkflowSpec }) =>
      apiFetch<WorkflowSummary>(base(customerId), { method: "POST", body: { spec } satisfies SaveWorkflowBody }),
    onSuccess: (_d, vars) => afterSave(qc, vars.customerId, vars.spec.slug),
  });
}

/** PUT: the whole spec replaced (the slug in the body must match the URL). */
export function useSaveWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    // base_version: the version this draft was loaded or last saved at, so a save over a change made
    // elsewhere meanwhile (a Deploy) answers 409 changed_elsewhere where Deployments are on
    mutationFn: ({ customerId, spec }: { customerId: string; spec: WorkflowSpec }) =>
      apiFetch<WorkflowSummary>(one(customerId, spec.slug), { method: "PUT", body: { spec, base_version: spec.version } satisfies SaveWorkflowBody }),
    onSuccess: (_d, vars) => afterSave(qc, vars.customerId, vars.spec.slug),
  });
}

/** 409 `runs_live` while a run of it is still live. */
export function useDeleteWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, slug }: { customerId: string; slug: string }) =>
      apiFetch<void>(one(customerId, slug), { method: "DELETE" }),
    onSuccess: (_d, vars) => {
      qc.removeQueries({ queryKey: workflowKeys.one(vars.customerId, vars.slug) });
      invalidateWorkflows(qc, vars.customerId);
    },
  });
}

/**
 * Server-side validation of an unsaved spec (the editor's Check button); 422
 * `invalid_workflow`. A valid one comes back with `notes`: the subagents and
 * named sources this workspace does not have. Nothing is written.
 */
export function useValidateWorkflow() {
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, spec }: { customerId: string; spec: WorkflowSpec }) =>
      apiFetch<WorkflowSummary>(`${base(customerId)}/validate`, { method: "POST", body: { spec } satisfies SaveWorkflowBody }),
  });
}

/** POST …/workflows/{slug}/runs; 422 `bad_params` for a value the parameter refuses. */
export function useStartWorkflowRun() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, slug, params }: { customerId: string; slug: string; params: Record<string, unknown> }) =>
      apiFetch<StartRunResponse>(`${one(customerId, slug)}/runs`, { method: "POST", body: { params } satisfies StartRunBody }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: [...workflowKeys.all(vars.customerId), "runs"] });
      invalidateWorkflows(qc, vars.customerId, vars.slug);
    },
  });
}

/**
 * The most runs a reader of days asks for (the route's ceiling): a subagent's
 * 30 days of runs (its header's sparkline, its Work rows) and the Subagents
 * list's 14. The default 100 reached back eleven days on corvusresearch's
 * 29 Oct runs; a reader that gets this many back may still not reach its
 * window (activityModel.knownFrom says from when it is whole).
 */
export const RUNS_WINDOW_LIMIT = 500;

/**
 * Runs, newest first, optionally of one workflow (`workflow`) or only the
 * live ones (`live`), the newest `limit` (the server's 100 when not given).
 * Polls every 10 s while any listed run is live, else every 30 s.
 */
export function useWorkflowRuns(customerId: string | null, { workflow, live, limit, poll }: { workflow?: string | null; live?: boolean; limit?: number; poll?: number } = {}) {
  const q = new URLSearchParams();
  if (workflow) q.set("workflow", workflow);
  if (live) q.set("live", "true");
  if (limit) q.set("limit", String(limit));
  const qs = q.toString();
  return useQuery({
    queryKey: workflowKeys.runs(customerId, workflow ?? null, !!live, limit ?? 0),
    enabled: !!customerId,
    queryFn: ({ signal }) =>
      apiFetch<RunListResponse>(`/api/customers/${encodeURIComponent(customerId!)}/workflow-runs${qs ? `?${qs}` : ""}`, { signal }),
    // a run a step of another workflow started is that run's to show, never a list's (the server leaves them out too)
    select: (d: RunListResponse) => (d.runs.some((r) => (r as { parent?: unknown }).parent) ? { ...d, runs: d.runs.filter((r) => !(r as { parent?: unknown }).parent) } : d),
    staleTime: 5_000,
    // `poll`: a badge reads slowly; a screen that shows the rows keeps this cadence
    refetchInterval: (query) => poll ?? ((query.state.data?.runs ?? []).some((r) => isLiveRunStatus(r.status)) ? 10_000 : 30_000),
    refetchOnWindowFocus: true,
  });
}

export function useWorkflowTemplates(customerId: string | null) {
  return useQuery({
    queryKey: workflowKeys.templates(customerId),
    enabled: !!customerId,
    queryFn: ({ signal }) => apiFetch<TemplateListResponse>(`/api/customers/${encodeURIComponent(customerId!)}/workflow-templates`, { signal }),
    staleTime: 30_000,
  });
}

/** Installs a template: the subagents it needs are created when missing, then the workflow. */
export function useInstallWorkflowTemplate() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, templateId, body }: { customerId: string; templateId: string; body?: InstallTemplateBody }) =>
      apiFetch<InstallTemplateResponse>(
        `/api/customers/${encodeURIComponent(customerId)}/workflow-templates/${encodeURIComponent(templateId)}/install`,
        { method: "POST", body: body ?? {} },
      ),
    onSuccess: (res, vars) => {
      invalidateWorkflows(qc, vars.customerId, res.workflow.slug);
      if (res.created_subagents.length) {
        void qc.invalidateQueries({ queryKey: ["subagent-blueprints", vars.customerId] });
        void qc.invalidateQueries({ queryKey: ["configured-agents"] });
        void qc.invalidateQueries({ queryKey: ["customer-agents"] });
      }
    },
  });
}

/**
 * The models a stage (or the handover step) may be given: what the
 * customer's gateway offers (routes/workflow_live.py), with the thinking
 * levels. Rarely changes; a failed read leaves the editor's free-text box.
 */
export function useWorkflowModels(customerId: string | null) {
  return useQuery({
    queryKey: workflowKeys.models(customerId),
    enabled: !!customerId,
    queryFn: ({ signal }) => apiFetch<ModelsResponse>(`/api/customers/${encodeURIComponent(customerId!)}/workflow-models`, { signal }),
    staleTime: 5 * 60_000,
    retry: (count, error) => !notFound(error) && count < 1,
  });
}

// ─── storage (Settings → General; services/jobs/cleanup.storage) ───

/** One subagent's runs/ folder and its sandbox: running, offline, "stopped (idle)" or none. */
export interface WorkflowStorageSubagent {
  subagent: string;
  runs: number;
  bytes: number;
  sandbox: string;
  idle_since: string;
  last_ok_at: string;
  pipeline_only: boolean;
}

/** The schema types the route as a plain object; its shape, as cleanup.storage writes it. */
export interface WorkflowStorage {
  subagents: WorkflowStorageSubagent[];
  run_store_bytes: number;
  runs: number;
  last_sweep_at: string;
  /** The last sweep's counts: folders, folder_bytes, large_files, large_bytes, store_bytes, sessions, runs, kept. */
  last_sweep: Record<string, number>;
  retention: { large_file_days: number; step_folder_days: number; run_store_days: number; idle_minutes: number };
}

/** Disk per subagent and in the run store, sandbox states, the last sweep. Read on open, never polled. */
export function useWorkflowStorage(customerId: string | null) {
  return useQuery({
    queryKey: workflowKeys.storage(customerId),
    enabled: !!customerId,
    queryFn: ({ signal }) => apiFetch<WorkflowStorage>(`${base(customerId!)}/storage`, { signal }),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });
}

// ─── data-source files ───

export function useWorkflowFiles(customerId: string | null, slug: string | null) {
  return useQuery({
    queryKey: workflowKeys.files(customerId, slug),
    enabled: !!customerId && !!slug,
    queryFn: ({ signal }) => apiFetch<{ files: WorkflowFile[] }>(`${one(customerId!, slug!)}/files`, { signal }),
    staleTime: 5_000,
    retry: (count, error) => !notFound(error) && count < 1,
  });
}

async function uploadWorkflowFile({ customerId, slug, file }: { customerId: string; slug: string; file: File }): Promise<WorkflowFile> {
  const form = new FormData();
  form.append("file", file, file.name);
  const resp = await rawFetch(`${one(customerId, slug)}/files`, { method: "POST", body: form, credentials: "include" });
  const text = await resp.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { error: "non_json_response", snippet: text.slice(0, 400) };
    }
  }
  if (!resp.ok) throw new ApiError(resp.status, `upload failed: ${resp.status}`, parsed);
  return parsed as WorkflowFile;
}

/** One file, multipart field `file`; 413/422 `file_refused`. */
export function useUploadWorkflowFile() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: uploadWorkflowFile,
    onSuccess: (_d, vars) => void qc.invalidateQueries({ queryKey: workflowKeys.files(vars.customerId, vars.slug) }),
  });
}

export function useDeleteWorkflowFile() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, slug, name }: { customerId: string; slug: string; name: string }) =>
      apiFetch<void>(`${one(customerId, slug)}/files/${encodeURIComponent(name)}`, { method: "DELETE" }),
    // A failure too re-reads the list: a 404 means the file was already gone.
    onSettled: (_d, _e, vars) => void qc.invalidateQueries({ queryKey: workflowKeys.files(vars.customerId, vars.slug) }),
  });
}

// ─── errors ───

/** The service's refusal code (`{detail: {code}}`), or null for anything else. */
export function workflowErrorCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const detail = (error.body as { detail?: unknown } | null)?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const code = (detail as { code?: unknown }).code;
    if (typeof code === "string" && code) return code;
  }
  return null;
}

/**
 * The sentence to show for a failed call: the service's own message for a
 * refusal, the first field error for a body the schema rejected (422
 * `{detail: [{loc, msg}]}`), "the server hit an error" for a crash, else
 * the transport message.
 */
export function workflowError(error: unknown): string {
  if (error instanceof ApiError) {
    if ((error.body as { error?: unknown } | null)?.error === "non_json_response") {
      return `The server hit an error (${error.status}). Reload and try again.`;
    }
    const detail = (error.body as { detail?: unknown } | null)?.detail;
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string" && message) return message;
    }
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { loc?: unknown[]; msg?: unknown };
      const field = Array.isArray(first.loc) ? first.loc.filter((p) => typeof p === "string" && p !== "body").join(" › ") : "";
      if (typeof first.msg === "string") return field ? `${field}: ${first.msg}` : first.msg;
    }
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}
